/**
 * Monta e desmonta o simulador legado dentro da casca.
 *
 * POR QUE ISTO NÃO É UM `import`
 * `apps/web/publico/legado/simulador.js` é **script clássico**, não módulo — e o cabeçalho
 * dele explica por quê (`lote()` troca `window.renderUnidades` em tempo de execução, o que
 * um módulo ES tornaria inofensivo e a colagem em massa passaria a renderizar por célula,
 * em silêncio). Script clássico não se importa: se carrega com uma etiqueta `<script>`, e
 * as dependências chegam por `window`.
 *
 * A ordem aqui é contrato, não estilo:
 *   1. pendurar as pontes no `window`   ← o script exige as duas e morre sem elas
 *   2. injetar a marcação                ← o script busca elementos por id ao arrancar
 *   3. carregar o script
 *   4. chamar `montarSimulador(dados)`
 * Inverter 2 e 3 daria uma tela em branco sem erro no console.
 */
import * as MOTOR from './motor-ponte';
import * as DADOS from './dados-ponte';
import * as UI from './ui-ponte';
import * as PLANILHA from './planilha-ponte';
import * as DOCS from './documentos-ponte';
import * as ANALISE from './analise-ponte';
import { desligarBalaoInfo, fecharBalaoInfo, ligarBalaoInfo, marcarNomesCortados } from '../ui/balao-info';
import type { EntradaMotor } from '../../../../packages/dados/src/mapeamento';
import type { PremissasMacro } from '../../../../engine/index';

const URL_SCRIPT = '/legado/simulador.js';
const URL_CONTEUDO = '/legado/conteudo.html';
const URL_SOBREPOSICOES = '/legado/sobreposicoes.html';

/** O cenário no formato que o simulador legado usa internamente. */
export interface CenarioLegado {
  id: string;
  empId: string;
  nome: string;
  lev: EntradaMotor['lev'];
  temFluxo: boolean;
  /**
   * O cadastro DESTE cenário, como o banco o devolve (09/10/2026, revisão do item 7; desde a
   * 0032, 12/10/2026, o cadastro é de cada cenário). Só os IRMÃOS levam: o simulador guarda UM
   * empreendimento vivo — o do cenário aberto —, e `aplicarTravasDemanda` escreve nele as
   * unidades, o VGV e as permutadas da tabela DO ABERTO. Derivada a partir dele, a coluna de um
   * irmão sem tabela (ou sem avaliação) herdava esses números e mudava conforme o cenário
   * aberto; a tela do irmão aberto sozinho e o relatório leem o cadastro dele, do banco. Publicar
   * o aberto não muda o de nenhum irmão. Ausente = usa o objeto vivo (o aberto, e o "Otimizado"
   * do otimizador, que nasce dele).
   */
  emp?: EntradaMotor['empreendimento'];
}

/**
 * O que o simulador precisa saber sobre QUEM está olhando.
 *
 * Só o mínimo, e de propósito: o simulador não decide permissão — quem decide é o banco
 * (RLS). Isto aqui governa apenas o que aparece na tela, e esconder um botão nunca é a
 * trava de segurança de nada.
 */
export interface PerfilSimulador {
  /**
   * Admin da plataforma: vê o otimizador e a memória de cálculo e, desde 09/10/2026, o
   * Guiado, o Acompanhamento, as Premissas macro, as abas "Fluxo" e "Fluxo mensal", a coluna
   * "Sugestão" das tabelas de unidades e a linha "Antecipação PJ" da Comparação. Para os
   * demais a vista lembrada que apontar para algo disso cai em Composição / Comparação.
   */
  isAdminHype: boolean;
}

export interface DadosSimulador {
  empreendimento: EntradaMotor['empreendimento'];
  rascunho: CenarioLegado;
  cenarios: CenarioLegado[];
  premissas: PremissasMacro;
  perfil: PerfilSimulador;
  /** Onde a pessoa estava da última vez neste cenário (null = abrir no começo). */
  vista: VistaSimulador | null;
  /** O que a publicação precisa e só a casca sabe. */
  publicacao: DADOS.ContextoPublicacao;
  /** De qual empreendimento (e cenário) o arquivo anexado passa a ser. */
  documentos: DOCS.ContextoDocumentos;
  /** Quem abre o diálogo do relatório comparativo (o atalho da Comparação). Ausente = sem atalho. */
  relatorio?: ANALISE.ContextoRelatorioPonte | null;
}

/** O lugar onde a pessoa parou. Não é dado do cenário — não vai para o servidor. */
export interface VistaSimulador {
  area: string;
  simSub: string;
  flcSub: string;
  painel: string | null;
  rolagem: number;
}

interface JanelaComSimulador {
  __fincastMotor?: typeof MOTOR;
  __fincastDados?: typeof DADOS;
  __fincastUI?: typeof UI;
  __fincastPlanilha?: typeof PLANILHA;
  __fincastDocs?: typeof DOCS;
  __fincastAnalise?: typeof ANALISE;
  montarSimulador?: (dados: DadosSimulador) => void;
  atualizarDadosSimulador?: (dados: DadosSimulador) => boolean;
  desmontarSimulador?: () => void;
  capturarVistaSimulador?: () => VistaSimulador | null;
}

const janela = window as unknown as Window & JanelaComSimulador;

/* O script e a marcação são buscados uma vez por sessão e reusados: trocar de cenário
   não deve baixar 220 KB de novo. */
let scriptCarregado: Promise<void> | null = null;
let marcacao: Promise<{ conteudo: string; sobreposicoes: string }> | null = null;

async function texto(url: string): Promise<string> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Não consegui carregar a tela de simulação (${url}: ${r.status}).`);
  return r.text();
}

function carregarScript(): Promise<void> {
  if (scriptCarregado !== null) return scriptCarregado;
  scriptCarregado = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = URL_SCRIPT;
    el.onload = () => resolve();
    el.onerror = () => {
      /* Zera para uma segunda tentativa poder acontecer — senão o erro fica grudado
         na sessão inteira e nem recarregar a tela resolve. */
      scriptCarregado = null;
      reject(new Error('Não consegui carregar a tela de simulação.'));
    };
    document.head.appendChild(el);
  });
  return scriptCarregado;
}

function carregarMarcacao() {
  if (marcacao !== null) return marcacao;
  marcacao = Promise.all([texto(URL_CONTEUDO), texto(URL_SOBREPOSICOES)])
    .then(([conteudo, sobreposicoes]) => ({ conteudo, sobreposicoes }))
    .catch((e) => {
      marcacao = null;
      throw e;
    });
  return marcacao;
}

/* ============================================================================
 * UMA MONTAGEM POR VEZ (07/08/2026)
 *
 * `montar()` tem três `await` no meio e escreve em estado GLOBAL: `window.__fincast*`,
 * o `innerHTML` do container, os filhos soltos no `<body>` e — o pior — o `estado` do
 * script clássico, que é único. Duas montagens no ar (clique duplo no cartão do cenário,
 * ida e volta rápida entre as listas, ou uma navegação chegando junto com o evento de
 * autenticação) intercalavam esses passos: quem chamasse `montarSimulador` por último
 * ficava com o modelo, e o DOM visível podia ser o da outra. A partir daí `syncOutputs`
 * reescreve os campos com números do cenário errado e `aplicarTravasDemanda` decide
 * `readOnly` pelo modelo errado — campo que muda sozinho e não deixa digitar.
 *
 * Duas travas, porque resolvem coisas diferentes:
 *   · a FILA garante que os passos de duas montagens nunca se intercalem;
 *   · a GERAÇÃO garante que a montagem já superada desista em vez de esperar a vez e
 *     desenhar por cima da tela nova.
 * ========================================================================== */
let filaDeMontagem: Promise<unknown> = Promise.resolve();
let geracaoMontagem = 0;

/**
 * Monta o simulador dentro de `container`.
 *
 * As sobreposições (modais e popovers) NÃO vão para o container: vão para o `<body>`.
 * São `position:fixed`, e a barra superior da casca usa `backdrop-filter`, que reancora
 * qualquer descendente `fixed` — o popover abriria deslocado, sem erro nenhum.
 */
export async function montar(container: HTMLElement, dados: DadosSimulador): Promise<void> {
  const minha = ++geracaoMontagem;
  const anterior = filaDeMontagem;
  const proxima = anterior
    .catch(() => undefined)
    .then(() => (minha === geracaoMontagem ? montarDeFato(container, dados, minha) : undefined));
  /* A fila engole o erro para que uma montagem que falhou não derrube a seguinte; quem
     chamou continua recebendo a exceção pelo `await` de baixo. */
  filaDeMontagem = proxima.catch(() => undefined);
  return proxima;
}

/** `true` enquanto esta montagem ainda é a mais recente. */
function aindaVale(minha: number): boolean {
  return minha === geracaoMontagem;
}

async function montarDeFato(container: HTMLElement, dados: DadosSimulador, minha: number): Promise<void> {
  janela.__fincastMotor = MOTOR;
  janela.__fincastDados = DADOS;
  janela.__fincastUI = UI;
  /* A ponte da planilha entra aqui, mas a BIBLIOTECA não: ela só é buscada no primeiro
     clique de importar ou exportar. É o ponto do lote — 639 KB que a v67 cobrava de
     toda visita passam a ser cobrados só de quem usa. */
  janela.__fincastPlanilha = PLANILHA;
  /* Guardar o arquivo dos modais de anexo. Como a planilha, é ponte fina: quem faz o
     trabalho é `packages/dados/src/documentos.ts`. */
  janela.__fincastDocs = DOCS;
  /* 09/10/2026 (relatório comparativo): a derivação da tabela por cenário e a análise por
     regras, as MESMAS do relatório — a Comparação e a síntese dela passam a usá-las. */
  janela.__fincastAnalise = ANALISE;
  ANALISE.configurarRelatorio(dados.relatorio ?? null);
  /* ANTES de carregar o script: o "Salvar" do simulador chama DADOS.publicar(), e sem
     isto ele erraria alto na primeira vez que alguém clicasse. */
  DADOS.configurarPublicacao(dados.publicacao);
  DOCS.configurarDocumentos(dados.documentos);

  const { conteudo, sobreposicoes } = await carregarMarcacao();
  if (!aindaVale(minha)) return;

  container.innerHTML = conteudo;

  desmontar({ apenasSobreposicoes: true });
  const berco = document.createElement('div');
  berco.id = 'fincast-sobreposicoes';
  berco.innerHTML = sobreposicoes;
  /* Os filhos vão soltos para o body, não dentro do berço: um contêiner intermediário
     serviria de âncora para os `fixed` e recriaria o problema que ele evita. */
  while (berco.firstChild) document.body.appendChild(berco.firstChild);

  await carregarScript();
  if (!aindaVale(minha)) return;

  if (typeof janela.montarSimulador !== 'function') {
    throw new Error('A tela de simulação carregou, mas não se apresentou. Recarregue a página.');
  }
  janela.montarSimulador(dados);
  montado = dados.rascunho.id;
  /* EL-195 (09/10/2026): o balão do "i" de ajuda e o nome do campo cortado. Depois do
     simulador, porque os rótulos só existem depois de `montarFormulario`. Os ouvintes são
     delegados no documento — `desmontar()` os tira. */
  ligarBalaoInfo();
  marcarNomesCortados(container);
}

/* Qual cenário o simulador tem montado AGORA (09/10/2026). O "Copiar" do seletor leva o
   que está na tela — e só pode fazer isso se a tela for mesmo a do cenário copiado: uma
   montagem que falhou deixaria a ponte de dados com o modelo do cenário ANTERIOR. */
let montado: string | null = null;

export function cenarioMontado(): string | null {
  return montado;
}

/**
 * Troca o modelo do simulador que já está montado, SEM refazer a tela (EL-190).
 *
 * É o que a sincronização entre pessoas usa: os números novos aparecem no lugar, com o
 * GRID, os ouvintes e a área onde a pessoa estava intactos. Devolve `false` quando o
 * momento não é bom (célula em edição, modal aberto, tabela em tela cheia) — aí quem
 * chamou tenta de novo mais tarde, em vez de puxar o chão.
 */
export function atualizar(dados: DadosSimulador): boolean {
  if (typeof janela.atualizarDadosSimulador !== 'function') return false;
  try {
    const trocou = janela.atualizarDadosSimulador(dados) === true;
    /* `montarFormulario` refez os rótulos: o balão aberto apontaria para um "i" que não
       existe mais, e os nomes novos ainda não foram medidos. */
    if (trocou) {
      fecharBalaoInfo();
      marcarNomesCortados();
    }
    return trocou;
  } catch {
    /* Atualizar é conforto: se falhar, a tela continua com o que já mostrava — que é
       exatamente o comportamento de antes deste lote. */
    return false;
  }
}

/**
 * Onde a pessoa estava, para a próxima montagem repor. Devolve `null` se o simulador
 * não estiver montado — sair de uma tela que nem chegou a abrir não apaga a memória
 * de quando ela abriu.
 */
export function capturarVista(): VistaSimulador | null {
  if (typeof janela.capturarVistaSimulador !== 'function') return null;
  try {
    return janela.capturarVistaSimulador();
  } catch {
    /* Memória de tela é conforto, não dado: se falhar, a próxima abertura começa do
       início — o que é exatamente o comportamento de antes deste lote. */
    return null;
  }
}

/** Chamada ao sair da tela. Solta o que o simulador pendurou no documento. */
export function desmontar(opcoes: { apenasSobreposicoes?: boolean } = {}): void {
  if (opcoes.apenasSobreposicoes !== true) {
    /* A tela está saindo: uma montagem ainda em voo deixa de valer aqui, senão ela
       terminaria depois e desenharia o cenário antigo por cima do que veio depois. */
    geracaoMontagem++;
    montado = null;
    /* o atalho do relatório falava com a tela que está saindo */
    ANALISE.configurarRelatorio(null);
    /* Antes do `return` de baixo: o balão é da casca, não do legado, e o `desmontarSimulador`
       não sabe dele. */
    desligarBalaoInfo();
    if (typeof janela.desmontarSimulador === 'function') {
      janela.desmontarSimulador();
      return;
    }
  }
  document
    .querySelectorAll('body > .ov, body > .vaga-pop, body > .treemenu, body > .gproxy, body > .gunsaved, body > .gctx, body > .gfull')
    .forEach((n) => n.remove());
}
